import { memo } from 'react'
import { Mic } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, Logo } from '@highlight/ui'
import { parseAudioNoteUri } from '@highlight/common/product'
import { faviconDomain, FaviconImage } from '@renderer/components/favicon-image'
import { providerLogo } from '@renderer/utils/provider-logo'
import type { BriefLink } from '../types'

interface LinkPillProps {
  link: BriefLink
  className?: string
  /** Render as a non-clickable visual chip (no href, no hover state, no arrow). */
  disabled?: boolean
}

interface BriefLinkIconProps {
  url: string
  /** Provenance source. Falls back to the link's favicon when it names no glyph. */
  source?: string
  /** Pixel size. Inline because the hover card wants a bigger glyph than the pill. */
  size?: number
  className?: string
}

/** Source mark, audio-note glyph or favicon for a brief link, shared by the pill and its hover card. */
export function BriefLinkIcon({ url, source, size = 14, className }: BriefLinkIconProps) {
  const isAudioNote = parseAudioNoteUri(url) !== null
  const logo = providerLogo(source?.trim().toLowerCase())

  return (
    <span
      style={{ width: size, height: size }}
      className={twMerge(
        'corner-smooth flex aspect-square shrink-0 items-center justify-center overflow-hidden rounded-[4px]',
        className,
      )}
    >
      {isAudioNote ? (
        <Mic size={size} className="text-green" />
      ) : logo ? (
        <Logo name={logo} size={size} />
      ) : (
        <FaviconImage domain={faviconDomain(url)} size={size} />
      )}
    </span>
  )
}

export const LinkPill = memo(function LinkPill({ link, className, disabled = false }: LinkPillProps) {
  const linkIcon = <BriefLinkIcon url={link.url} source={link.source} />

  if (disabled) {
    return (
      <Button
        href={link.url}
        title={link.title}
        variant="outline"
        left={linkIcon}
        className={twMerge('border-border pointer-events-none max-w-full', className)}
      >
        <span className="min-w-0 truncate">{link.title}</span>
      </Button>
    )
  }

  return (
    <Button
      href={link.url}
      target="_blank"
      rel="noreferrer"
      title={link.title}
      variant="outline"
      left={linkIcon}
      className={twMerge('border-border max-w-full', className)}
    >
      <span className="min-w-0 truncate">{link.title}</span>
    </Button>
  )
})
